import { sb } from '../supabase';
import { listViolations, violationToItem } from './consoleContactViolations';

// Unified admin activity inbox. TRADER actions come from admin_events — one row
// per action, written only by database triggers
// (20261011120000_admin_trader_events.sql), so a request submitted from the app
// or a closed tab is never missed. The rest (supplier registrations, catalog
// extractions, support tickets, bounced emails) is still derived from its own
// table. Each query is safe (degrades to empty on error) and capped; results
// merge into one time-sorted feed.
//
// ⚠️ CHANGED 2026-10-11 — the "new quote request" item used to be derived from
// request_factory_invites. Those rows are created by the ADMIN, so the feed
// announced the team's own actions and never a trader's request. Trader
// messages were derived from `messages` the same way. Both now read
// admin_events.

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
const safe = (p) => p.then((r) => r, () => ({ data: [] }));

const KIND_AR = { direct: 'طلب عرض سعر', factory: 'طلب تسعير لمصنع', managed: 'طلب مُدار', idea_to_product: 'صنّع فكرتك' };
const KIND_EN = { direct: 'Quote request', factory: 'Factory quote request', managed: 'Managed request', idea_to_product: 'Idea to product' };
const partyName = (p) => nf(p && (p.name || p.email || p.phone));

// One admin_events row -> one inbox item. Exported for the unit test.
export function eventToItem(e) {
  const s = e.summary || {};
  const trader = partyName(s.trader);
  const title = nf(s.titleAr) || nf(s.titleEn);
  const requestLink = e.request_id ? `/admin2/requests/${e.request_id}` : null;
  const base = { id: e.id, at: e.created_at, handled: !!e.handled_at, emailState: e.email_state };
  switch (e.kind) {
    case 'request_created':
      return { ...base, group: 'request', type: 'request',
        title_ar: `${KIND_AR[s.requestKind] || 'طلب'} جديد${trader ? ` — ${trader}` : ''}`,
        title_en: `New ${(KIND_EN[s.requestKind] || 'request').toLowerCase()}${trader ? ` — ${trader}` : ''}`,
        sub: [nf(s.requestRef), title].filter(Boolean).join(' · '), link: requestLink, tone: 'warn' };
    case 'request_cancelled':
      return { ...base, group: 'request', type: 'cancelled',
        title_ar: `تاجر ألغى طلبه${trader ? ` — ${trader}` : ''}`, title_en: `Trader cancelled a request${trader ? ` — ${trader}` : ''}`,
        sub: [nf(s.requestRef), title].filter(Boolean).join(' · '), link: requestLink, tone: 'danger' };
    case 'offer_decided': {
      const accepted = s.decision === 'accepted';
      return { ...base, group: 'request', type: accepted ? 'accepted' : 'rejected',
        title_ar: accepted ? 'تاجر قبل عرضًا' : 'تاجر رفض عرضًا', title_en: accepted ? 'Trader accepted an offer' : 'Trader rejected an offer',
        sub: [nf(s.requestRef), partyName(s.supplier), s.price ? `${s.price} ${nf(s.currency)}` : ''].filter(Boolean).join(' · '),
        link: requestLink, tone: accepted ? 'ok' : 'neutral' };
    }
    case 'purchase_request':
      return { ...base, group: 'request', type: 'purchase',
        title_ar: `طلب شراء مباشر${trader ? ` — ${trader}` : ''}`, title_en: `Purchase request${trader ? ` — ${trader}` : ''}`,
        sub: [nf(s.product), s.quantity ? `× ${s.quantity}` : '', partyName(s.supplier)].filter(Boolean).join(' · '),
        link: e.actor_id ? `/admin2/traders/${e.actor_id}` : null, tone: 'warn' };
    case 'conversation_started': {
      const toFactory = s.channel === 'factory';
      const other = toFactory ? nf(s.factory && s.factory.name) : partyName(s.supplier);
      const counterpart = toFactory ? e.factory_id : e.counterpart_id;
      const q = new URLSearchParams({ channel: toFactory ? 'factory' : 'supplier', trader: e.actor_id || '', counterpart: counterpart || '' });
      return { ...base, group: 'message', type: 'message',
        title_ar: `${trader || 'تاجر'} راسل ${toFactory ? 'مصنعًا' : 'موردًا'}${other ? ` — ${other}` : ''}`,
        title_en: `${trader || 'A trader'} messaged ${toFactory ? 'a factory' : 'a supplier'}${other ? ` — ${other}` : ''}`,
        sub: nf(s.preview).slice(0, 80), link: `/admin2/conversations?${q}`, tone: 'info' };
    }
    case 'market_order_hold':
      // a paid market order held before procurement (market_order_integrity)
      return { ...base, group: 'request', type: 'market_hold',
        title_ar: `طلب سوق موقوف للمراجعة — ${nf(s.orderNo)}`, title_en: `Market order held for review — ${nf(s.orderNo)}`,
        sub: [...new Set((Array.isArray(s.lines) ? s.lines : []).flatMap((l) => l.flags || []))].join(' · '),
        link: '/admin2/market-orders', tone: 'danger' };
    case 'ai_budget':
      // the day's Gemini spend reached 80% / 100% of the daily cap (ai_budget)
      return { ...base, group: 'request', type: 'ai_budget',
        title_ar: Number(s.threshold) >= 100 ? `سقف Gemini اليومي اكتمل — توقف حتى الغد (${nf(s.day)})` : `Gemini بلغ ${s.threshold ?? 80}% من السقف اليومي (${nf(s.day)})`,
        title_en: Number(s.threshold) >= 100 ? `Gemini daily cap reached — stopped until tomorrow (${nf(s.day)})` : `Gemini at ${s.threshold ?? 80}% of the daily cap (${nf(s.day)})`,
        sub: `$${s.usedUsd ?? '-'} / $${s.capUsd ?? '-'}`, link: '/admin2/market-ingestion', tone: 'warn' };
    case 'market_inquiry':
      // a trader asked a store supplier a question (market_inquiries)
      return { ...base, group: 'request', type: 'market_inquiry',
        title_ar: `سؤال جديد للمورد — ${nf(s.product)}`, title_en: `New supplier question — ${nf(s.product)}`,
        sub: nf(s.question), link: '/admin2/market-inquiries', tone: 'info' };
    case 'market_loss_budget':
      // the month's accepted loss reached 80% / 100% of the budget (market_loss_budget)
      return { ...base, group: 'request', type: 'market_loss_budget',
        title_ar: Number(s.threshold) >= 100 ? `ميزانية الخسارة اكتملت — ${nf(s.month)}` : `الخسارة بلغت ${s.threshold ?? 80}% من الميزانية — ${nf(s.month)}`,
        title_en: Number(s.threshold) >= 100 ? `Loss budget used up — ${nf(s.month)}` : `Loss at ${s.threshold ?? 80}% of the budget — ${nf(s.month)}`,
        sub: `${s.usedSar ?? '-'} / ${s.budgetSar ?? '-'}`, link: '/admin2/market-orders', tone: 'warn' };
    case 'fx_rate_alert': {
      // the daily CNY rate moved more than 1%, or the run failed (fx_cny_auto_rate)
      const failed = s.outcome === 'error' || s.outcome === 'rejected';
      return { ...base, group: 'request', type: 'fx_rate',
        title_ar: failed ? 'تحديث سعر اليوان فشل — السعر الحالي باقٍ' : `سعر اليوان تحرك ${s.changePct ?? '-'}% (${nf(s.prevRate)} ← ${nf(s.rate)})`,
        title_en: failed ? 'CNY rate update failed — current rate kept' : `CNY rate moved ${s.changePct ?? '-'}% (${nf(s.prevRate)} → ${nf(s.rate)})`,
        sub: nf(s.note || s.outcome), link: '/admin/settings', tone: failed ? 'danger' : 'warn' };
    }
    case 'market_storage_alert':
      // HioBuy's free storage runs out (market_storage_window)
      return { ...base, group: 'request', type: 'market_storage',
        title_ar: `التخزين المجاني قرب ينتهي — ${nf(s.orderNo)} (باقي ${s.daysLeft ?? '-'} يوم)`,
        title_en: `Free storage running out — ${nf(s.orderNo)} (${s.daysLeft ?? '-'} days left)`,
        sub: nf(s.method), link: '/admin2/market-waiting', tone: 'warn' };
    default:
      return null;
  }
}

// An inbox item that still wants a decision: a trader event nobody marked
// handled (`id` = admin_events row) or a contact violation nobody reviewed
// (`violationId`). Everything else in the feed has no handled state.
export const needsAction = (r) => !!(r && (r.id || r.violationId) && !r.handled);

// Trader actions nobody has marked handled — the sidebar badge.
export async function fetchUnhandledCount() {
  const { count, error } = await sb.from('admin_events')
    .select('id', { count: 'exact', head: true }).is('handled_at', null);
  if (error) throw error;
  return count || 0;
}

export async function markEventHandled(id, handled = true) {
  const { error } = await sb.rpc('admin_mark_event_handled', { p_event_id: id, p_handled: handled });
  if (error) throw error;
}

export async function fetchNotifications() {
  const [events, regs, imps, tickets, bounces, violations] = await Promise.all([
    safe(sb.from('admin_events')
      .select('id, kind, created_at, actor_id, request_id, factory_id, counterpart_id, summary, email_state, handled_at')
      .order('created_at', { ascending: false }).limit(100)),
    safe(sb.from('profiles').select('id, full_name, email, created_at')
      .eq('role', 'supplier').order('created_at', { ascending: false }).limit(20)),
    safe(sb.from('factory_catalog_imports').select('id, original_filename, catalog_title, status, updated_at, created_at')
      .in('status', ['extracted', 'failed']).order('updated_at', { ascending: false }).limit(30)),
    safe(sb.from('support_tickets').select('id, subject, status, created_at')
      .order('created_at', { ascending: false }).limit(20)),
    safe(sb.from('email_logs').select('id, recipient_email, template_name, status, created_at')
      .in('status', ['bounced', 'failed', 'complained']).order('created_at', { ascending: false }).limit(20)),
    // Contact violations (owner, 2026-10-15) — each masked contact attempt.
    // Settled on /admin2/contact-violations, never by this feed's «تم التعامل».
    listViolations('all', 50).then((data) => ({ data }), (e) => {
      console.error('[consoleNotifications] contact violations failed:', e?.message || e);
      return { data: [] };
    }),
  ]);

  const items = (events.data || []).map(eventToItem).filter(Boolean);
  (violations.data || []).map(violationToItem).filter(Boolean).forEach((v) => items.push(v));
  (regs.data || []).forEach((r) => items.push({ group: 'registered', type: 'registered', at: r.created_at, title_ar: 'مورّد سجّل', title_en: 'Supplier registered', sub: nf(r.full_name) || nf(r.email), link: '/admin2/suppliers', tone: 'ok' }));
  (imps.data || []).forEach((im) => {
    const failed = im.status === 'failed';
    items.push({ group: 'catalog', type: failed ? 'error' : 'extraction', at: im.updated_at || im.created_at, title_ar: failed ? 'فشل استخراج كتالوج' : 'اكتمل استخراج كتالوج', title_en: failed ? 'Extraction failed' : 'Catalog extracted', sub: nf(im.catalog_title) || nf(im.original_filename), link: `/admin/catalog-import/${im.id}`, tone: failed ? 'danger' : 'ok' });
  });
  (tickets.data || []).forEach((tk) => items.push({ group: 'support', type: 'support', at: tk.created_at, title_ar: 'تذكرة دعم', title_en: 'Support ticket', sub: nf(tk.subject), link: `/admin/support/${tk.id}`, tone: 'neutral' }));
  (bounces.data || []).forEach((b) => items.push({ group: 'bounce', type: 'bounce', at: b.created_at, title_ar: 'إيميل مرتد', title_en: 'Email bounced', sub: `${nf(b.recipient_email)} · ${nf(b.template_name)}`, link: '/admin/emails', tone: 'danger' }));

  return items.filter((x) => x.at).sort((a, b) => String(b.at).localeCompare(String(a.at)));
}
